import * as Sentry from "@sentry/react"
import { Suspense, lazy } from "react"
import { createRoot } from "react-dom/client"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { BrowserRouter, Routes, Route } from "react-router-dom"
import AppWrapper from "components/AppWrapper"
import { ThemeProvider } from "context/theme-provider"
import { CustomerProvider, getDevCustomerOverride, readLinkedCustomer } from "context/customer-provider"
import { ControlDeskProvider } from "context/control-desk-provider"
import Auth0ProviderWithHistory from "./auth/auth0-provider-with-history"
import { BootSnapshotProvider } from "boot/BootSnapshotProvider"
import { hydrateFromBootSnapshot, readBootSnapshot } from "boot/snapshot"
import config from "config"
import { defaultQueryRetry } from "api/queryDefaults"

// External pages (no Auth0 required)
const ExternalWorkItemPage = lazy(() => import("pages/external/WorkItem"))

//
import "styles/custom.css"
// mapbox styles global import
import "mapbox-gl/dist/mapbox-gl.css"

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: defaultQueryRetry } } })

// What the last session in this browser booted with (boot/snapshot.ts): the
// app renders from it at once while Auth0 and /me/bootstrap revalidate.
// Not on the magic-link pages (/work/…), which never sign in.
const bootSnapshot = window.location.pathname.startsWith("/work/")
  ? null
  : readBootSnapshot({
      clientId: config.auth0ClientId,
      preferredCustomer: readLinkedCustomer(window.location.search) ?? getDevCustomerOverride(),
    })
if (bootSnapshot) hydrateFromBootSnapshot(queryClient, bootSnapshot)
Sentry.init({
  dsn: "https://3db99a36861b42b3966a4a32a02dbf2b@o1003026.ingest.sentry.io/6538890",
  integrations: [Sentry.browserTracingIntegration()],
  enabled: import.meta.env.PROD,
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1,
})

// if (import.meta.env.PROD) console.log = () => {};

const container = document.getElementById("root")
const root = createRoot(container!)

root.render(
  <BrowserRouter>
    <QueryClientProvider client={queryClient}>
      <Routes>
        {/* External task page — outside Auth0, no authentication required */}
        <Route path="/work/:token" element={<Suspense fallback={<div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>Loading...</div>}><ExternalWorkItemPage /></Suspense>} />

        {/* All other routes — inside Auth0 */}
        <Route path="*" element={
          <Auth0ProviderWithHistory>
            <BootSnapshotProvider snapshot={bootSnapshot}>
              <CustomerProvider>
                <ControlDeskProvider>
                  <ThemeProvider>
                    <AppWrapper />
                  </ThemeProvider>
                </ControlDeskProvider>
              </CustomerProvider>
            </BootSnapshotProvider>
          </Auth0ProviderWithHistory>
        } />
      </Routes>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </BrowserRouter>,
)
